import { z } from 'zod';
import {
  DeliveryOverview,
  QueryMetadata,
  ReleaseCalendarView,
  type GlobalFilters,
  type QueryResult,
  type ReleaseGrouping,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * The `delivery` group of `Repositories`, backed by the real API
 * (`GET /delivery` in apps/api) instead of the in-memory fixture.
 *
 * Same posture as `httpPortfolioRepository.ts`: parsed through the real
 * `DeliveryOverview` / `QueryMetadata` schemas, not cast, and only routed for
 * the default (unfiltered, current-period) view since `/delivery` does not
 * accept `GlobalFilters` yet — see apps/api/src/routes/delivery.ts for which
 * fields this route can answer honestly and which stay `null`/`Unavailable`.
 */

const Envelope = z.object({ data: DeliveryOverview, meta: QueryMetadata });
const CalendarEnvelope = z.object({ data: ReleaseCalendarView, meta: QueryMetadata });

export function createHttpDeliveryRepository(baseUrl: string) {
  return {
    async getOverview(filters: GlobalFilters): Promise<QueryResult<DeliveryOverview>> {
      const response = await apiFetch(`${baseUrl}/delivery?${filterParams(filters)}`);
      if (!response.ok) {
        throw new Error(`GET /delivery failed: ${response.status} ${response.statusText}`);
      }
      const json: unknown = await response.json();
      return Envelope.parse(json);
    },

    /**
     * `GET /releases` — the portfolio release calendar.
     *
     * Grouping and horizon are sent alongside the global filters rather than
     * held in component state, because the screen keeps both in the URL: a
     * calendar somebody sends to a colleague has to arrive showing what the
     * sender was looking at.
     */
    async getReleaseCalendar(
      filters: GlobalFilters,
      options: { groupBy?: ReleaseGrouping; horizonMonths?: number } = {},
    ): Promise<QueryResult<ReleaseCalendarView>> {
      const params = filterParams(filters);
      if (options.groupBy) params.set('groupBy', options.groupBy);
      if (options.horizonMonths) params.set('horizonMonths', String(options.horizonMonths));
      const response = await apiFetch(`${baseUrl}/releases?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`GET /releases failed: ${response.status} ${response.statusText}`);
      }
      const json: unknown = await response.json();
      return CalendarEnvelope.parse(json);
    },
  };
}
